import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import subscriptionService from '../services/subscriptionService';
import { createSubscriptionData, validateSubscriptionParams } from '../utils/subscriptionUtils';
import quotaService from '../services/quotaService';
import userService from '../services/userService';

// Async thunks
export const fetchPlans = createAsyncThunk(
  'subscription/fetchPlans',
  async (_, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.listPlans();
      // Backend returns ApiEnvelope: { success: boolean, data: T, message?: string }
      const plansData = response.data || response;
      return plansData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserSubscription = createAsyncThunk(
  'subscription/fetchUserSubscription',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.getUserSubscription(userId);
      
      console.log('🔍 fetchUserSubscription - Raw response:', response);
      
      // Backend returns ApiEnvelope: { success: boolean, data: T, message?: string }
      // But subscriptionService already returns response.data (the ApiEnvelope)
      const envelope = response;
      
      // Handle case where API returns success: false with data: null
      if (envelope.success === false && envelope.data === null) {
        console.log('🔍 fetchUserSubscription - No subscription found');
        return null; // Return null instead of the response object
      }
      
      // Extract the actual subscription data from the envelope
      const subscriptionData = envelope.data;
      console.log('🔍 fetchUserSubscription - Parsed subscription data:', subscriptionData);
      
      return subscriptionData;
    } catch (error) {
      console.error('🔍 fetchUserSubscription - Error:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchUserQuota = createAsyncThunk(
  'subscription/fetchUserQuota',
  async (userId, { rejectWithValue, getState }) => {
    try {
      const response = await quotaService.getUserQuota(userId);
      
      // Backend returns ApiEnvelope: { success: boolean, data: T, message?: string }
      // But quotaService already returns response.data (the ApiEnvelope)
      const envelope = response;
      const quotaData = envelope.data || envelope;
      
      console.log('🔍 fetchUserQuota - Parsed quota data:', quotaData);
      
      // Get current subscription to add purchase credit information
      const state = getState();
      const currentSubscription = state.subscription.currentSubscription;
      
      // Update quota data with subscription plan information
      if (currentSubscription?.plan && currentSubscription.status === 'ACTIVE') {
        // Only update quota if subscription is ACTIVE
        // Update storage limit to match subscription plan
        if (currentSubscription.plan.storageLimitSeconds) {
          quotaData.limitSeconds = currentSubscription.plan.storageLimitSeconds;
          // Recalculate remaining seconds based on new limit
          quotaData.remainingSeconds = Math.max(0, 
            currentSubscription.plan.storageLimitSeconds - (quotaData.usedSeconds || 0)
          );
        }
        
        // Update purchase credit information
        if (currentSubscription.plan.freePurchaseSecondsPerMonth) {
          quotaData.limitCredits = currentSubscription.plan.freePurchaseSecondsPerMonth;
          // TODO: Backend should provide usedCredits and remainingCredits
          // For now, set them to 0 as placeholder
          quotaData.usedCredits = quotaData.usedCredits || 0;
          quotaData.remainingCredits = quotaData.remainingCredits || currentSubscription.plan.freePurchaseSecondsPerMonth;
        } else {
          quotaData.limitCredits = 0;
          quotaData.usedCredits = 0;
          quotaData.remainingCredits = 0;
        }
      } else if (currentSubscription?.status === 'CANCELLED' || currentSubscription?.status === 'EXPIRED') {
        // Reset quota to free tier when subscription is cancelled or expired
        quotaData.limitSeconds = 10800; // 3 hours (free tier)
        quotaData.remainingSeconds = Math.max(0, 10800 - (quotaData.usedSeconds || 0));
        quotaData.limitCredits = 0;
        quotaData.usedCredits = 0;
        quotaData.remainingCredits = 0;
      }
      
      console.log('🔍 fetchUserQuota - Final quota data to be stored:', quotaData);
      
      return quotaData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const changePlan = createAsyncThunk(
  'subscription/changePlan',
  async ({ userId, planCode }, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.changePlan(userId, planCode);
      // Backend returns ApiEnvelope: { success: boolean, data: T, message?: string }
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Start a free subscription for a user
export const startFreeSubscription = createAsyncThunk(
  'subscription/startFreeSubscription',
  async ({ userId, planCode = 'FREE' }, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.startFreeSubscription(userId, planCode);
      // Backend returns ApiEnvelope<SubscriptionDto>
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Note: toggleAutoRenew removed - requires stripe subscription ID
// Use setBillingAutoRenew(stripeSubId, enabled) instead

export const startTrial = createAsyncThunk(
  'subscription/startTrial',
  async ({ userId, planCode }, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.startTrial(userId, planCode);
      // Backend returns ApiEnvelope: { success: boolean, data: T, message?: string }
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const upgradeSubscription = createAsyncThunk(
  'subscription/upgradeSubscription',
  async ({ userId, planCode }, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.upgradeSubscription(userId, planCode);
      // Backend returns ApiEnvelope: { success: boolean, data: T, message?: string }
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscription/cancelSubscription',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.cancelSubscription(userId);
      // Backend returns void (204 No Content), service returns { success: true }
      return { userId, success: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchFeatureConfig = createAsyncThunk(
  'subscription/fetchFeatureConfig',
  async (_, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.getFeatureConfig();
      // Backend returns ApiEnvelope: { success: boolean, data: T, message?: string }
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateFeatureConfig = createAsyncThunk(
  'subscription/updateFeatureConfig',
  async (config, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.updateFeatureConfig(config);
      // Backend returns ApiEnvelope: { success: boolean, data: T, message?: string }
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Billing actions (Admin only)
export const startBillingSubscription = createAsyncThunk(
  'subscription/startBillingSubscription',
  async (billingData, { rejectWithValue }) => {
    try {
      // Validate parameters before sending
      const validation = validateSubscriptionParams(billingData);
      if (!validation.success) {
        return rejectWithValue(validation.errors.join(', '));
      }
      
      // Use the consolidated logic for consistent parameter handling
      const processedBillingData = createSubscriptionData(billingData);
      
      console.log('startBillingSubscription - processed billing data:', processedBillingData);
      
      const response = await subscriptionService.startBillingSubscription(processedBillingData);
      // Backend returns ApiEnvelope<SubscriptionChargeResult>
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const cancelBillingSubscription = createAsyncThunk(
  'subscription/cancelBillingSubscription',
  async ({ stripeSubId, userId }, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.cancelBillingSubscription(stripeSubId, userId);
      // Backend returns void (204 No Content), service returns { success: true }
      return { stripeSubId, userId, success: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const setBillingAutoRenew = createAsyncThunk(
  'subscription/setBillingAutoRenew',
  async ({ stripeSubId, enabled }, { rejectWithValue }) => {
    try {
      const response = await subscriptionService.setBillingAutoRenew(stripeSubId, enabled);
      // Backend returns void (204 No Content), service returns { success: true }
      return { stripeSubId, enabled, success: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// User management for admin subscription management
export const fetchAdminUsers = createAsyncThunk(
  'subscription/fetchAdminUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getAdminUsers();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  plans: [],
  currentSubscription: null,
  currentQuota: null,
  featureConfig: null,
  loading: false,
  plansLoading: false,
  subscriptionLoading: false,
  quotaLoading: false,
  error: null,
  adminUsers: [],
  adminLoading: false,
  adminError: null,
  billingLoading: false,
  billingError: null,
  subscriptionRefreshTrigger: null
};

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.adminError = null;
    },
    clearSubscription: (state) => {
      state.currentSubscription = null;
    },
    updateQuotaWithSubscription: (state) => {
      // Update quota data with current subscription plan information
      if (state.currentQuota && state.currentSubscription?.plan && state.currentSubscription.status === 'ACTIVE') {
        // Only update quota if subscription is ACTIVE
        // Update storage limit to match subscription plan
        if (state.currentSubscription.plan.storageLimitSeconds) {
          state.currentQuota.limitSeconds = state.currentSubscription.plan.storageLimitSeconds;
          // Recalculate remaining seconds based on new limit
          state.currentQuota.remainingSeconds = Math.max(0, 
            state.currentSubscription.plan.storageLimitSeconds - (state.currentQuota.usedSeconds || 0)
          );
        }
        
        // Update purchase credit information
        if (state.currentSubscription.plan.freePurchaseSecondsPerMonth) {
          state.currentQuota.limitCredits = state.currentSubscription.plan.freePurchaseSecondsPerMonth;
          // Keep existing usedCredits if available, otherwise set to 0
          state.currentQuota.usedCredits = state.currentQuota.usedCredits || 0;
          // Calculate remaining credits
          state.currentQuota.remainingCredits = Math.max(0, 
            state.currentSubscription.plan.freePurchaseSecondsPerMonth - (state.currentQuota.usedCredits || 0)
          );
        }
      } else if (state.currentQuota && state.currentSubscription?.status === 'CANCELLED' || state.currentSubscription?.status === 'EXPIRED') {
        // Reset quota to free tier when subscription is cancelled or expired
        state.currentQuota.limitSeconds = 10800; // 3 hours (free tier)
        state.currentQuota.remainingSeconds = Math.max(0, 10800 - (state.currentQuota.usedSeconds || 0));
        state.currentQuota.limitCredits = 0;
        state.currentQuota.usedCredits = 0;
        state.currentQuota.remainingCredits = 0;
      }
    },
    updateCreditsUsage: (state, action) => {
      // Update credits usage when a purchase is made
      const { creditsUsed } = action.payload;
      if (state.currentQuota && creditsUsed > 0) {
        state.currentQuota.usedCredits = (state.currentQuota.usedCredits || 0) + creditsUsed;
        state.currentQuota.remainingCredits = Math.max(0, 
          (state.currentQuota.limitCredits || 0) - state.currentQuota.usedCredits
        );
      }
    },
    refreshSubscriptionData: (state) => {
      // Mark that subscription data should be refreshed
      state.subscriptionRefreshTrigger = Date.now();
    },
    resetQuotaToFreeTier: (state) => {
      // Reset quota to free tier (4 hours storage, no purchase credits)
      if (state.currentQuota) {
        state.currentQuota.limitSeconds = 10800; // 3 hours (free tier)
        state.currentQuota.remainingSeconds = Math.max(0, 10800 - (state.currentQuota.usedSeconds || 0));
        state.currentQuota.limitCredits = 0;
        state.currentQuota.usedCredits = 0;
        state.currentQuota.remainingCredits = 0;
      }
    }
  },
  extraReducers: (builder) => {
    // Plans
    builder
      .addCase(fetchPlans.pending, (state) => {
        state.plansLoading = true;
        state.error = null;
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.plansLoading = false;
        state.plans = action.payload;
      })
      .addCase(fetchPlans.rejected, (state, action) => {
        state.plansLoading = false;
        state.error = action.payload;
      });

    // User subscription
    builder
      .addCase(fetchUserSubscription.pending, (state) => {
        state.subscriptionLoading = true;
        state.error = null;
      })
                .addCase(fetchUserSubscription.fulfilled, (state, action) => {
        state.subscriptionLoading = false;
        state.currentSubscription = action.payload;
        
        // Update quota data with subscription plan information if quota exists
        if (state.currentQuota && action.payload?.plan && action.payload.status === 'ACTIVE') {
          // Only update quota if subscription is ACTIVE
          // Update storage limit to match subscription plan
          if (action.payload.plan.storageLimitSeconds) {
            state.currentQuota.limitSeconds = action.payload.plan.storageLimitSeconds;
            // Recalculate remaining seconds based on new limit
            state.currentQuota.remainingSeconds = Math.max(0, 
              action.payload.plan.storageLimitSeconds - (state.currentQuota.usedSeconds || 0)
            );
          }
          
          // Update purchase credit information
          if (action.payload.plan.freePurchaseSecondsPerMonth) {
            state.currentQuota.limitCredits = action.payload.plan.freePurchaseSecondsPerMonth;
            state.currentQuota.usedCredits = state.currentQuota.usedCredits || 0;
            state.currentQuota.remainingCredits = Math.max(0, 
              action.payload.plan.freePurchaseSecondsPerMonth - (state.currentQuota.usedCredits || 0)
            );
          }
        } else if (state.currentQuota && (action.payload?.status === 'CANCELLED' || action.payload?.status === 'EXPIRED')) {
          // Reset quota to free tier when subscription is cancelled or expired
          state.currentQuota.limitSeconds = 10800; // 3 hours (free tier)
          state.currentQuota.remainingSeconds = Math.max(0, 10800 - (state.currentQuota.usedSeconds || 0));
          state.currentQuota.limitCredits = 0;
          state.currentQuota.usedCredits = 0;
          state.currentQuota.remainingCredits = 0;
        }
      })
          .addCase(fetchUserSubscription.rejected, (state, action) => {
      state.subscriptionLoading = false;
      state.error = action.payload;
    });

    // User quota
    builder
      .addCase(fetchUserQuota.pending, (state) => {
        state.quotaLoading = true;
        state.error = null;
      })
      .addCase(fetchUserQuota.fulfilled, (state, action) => {
        state.quotaLoading = false;
        state.currentQuota = action.payload;
      })
      .addCase(fetchUserQuota.rejected, (state, action) => {
        state.quotaLoading = false;
        state.error = action.payload;
      });

    // Change plan
    builder
      .addCase(changePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSubscription = action.payload;
        
        // Immediately update quota data with new subscription plan information
        if (state.currentQuota && action.payload?.plan && action.payload.status === 'ACTIVE') {
          // Only update quota if subscription is ACTIVE
          // Update storage limit to match subscription plan
          if (action.payload.plan.storageLimitSeconds) {
            state.currentQuota.limitSeconds = action.payload.plan.storageLimitSeconds;
            // Recalculate remaining seconds based on new limit
            state.currentQuota.remainingSeconds = Math.max(0, 
              action.payload.plan.storageLimitSeconds - (state.currentQuota.usedSeconds || 0)
            );
          }
          
          // Update purchase credit information
          if (action.payload.plan.freePurchaseSecondsPerMonth) {
            state.currentQuota.limitCredits = action.payload.plan.freePurchaseSecondsPerMonth;
            state.currentQuota.usedCredits = state.currentQuota.usedCredits || 0;
            state.currentQuota.remainingCredits = Math.max(0, 
              action.payload.plan.freePurchaseSecondsPerMonth - (state.currentQuota.usedCredits || 0)
            );
          }
        } else if (state.currentQuota && (action.payload?.status === 'CANCELLED' || action.payload?.status === 'EXPIRED')) {
          // Reset quota to free tier when subscription is cancelled or expired
          state.currentQuota.limitSeconds = 10800; // 3 hours (free tier)
          state.currentQuota.remainingSeconds = Math.max(0, 10800 - (state.currentQuota.usedSeconds || 0));
          state.currentQuota.limitCredits = 0;
          state.currentQuota.usedCredits = 0;
          state.currentQuota.remainingCredits = 0;
        }
      })
      .addCase(changePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Start free subscription
    builder
      .addCase(startFreeSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startFreeSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSubscription = action.payload;
        
        // Immediately update quota data with new subscription plan information
        if (state.currentQuota && action.payload?.plan && action.payload.status === 'ACTIVE') {
          // Only update quota if subscription is ACTIVE
          // Update storage limit to match subscription plan
          if (action.payload.plan.storageLimitSeconds) {
            state.currentQuota.limitSeconds = action.payload.plan.storageLimitSeconds;
            // Recalculate remaining seconds based on new limit
            state.currentQuota.remainingSeconds = Math.max(0, 
              action.payload.plan.storageLimitSeconds - (state.currentQuota.usedSeconds || 0)
            );
          }
          
          // Update purchase credit information
          if (action.payload.plan.freePurchaseSecondsPerMonth) {
            state.currentQuota.limitCredits = action.payload.plan.freePurchaseSecondsPerMonth;
            state.currentQuota.usedCredits = state.currentQuota.usedCredits || 0;
            state.currentQuota.remainingCredits = Math.max(0, 
              action.payload.plan.freePurchaseSecondsPerMonth - (state.currentQuota.usedCredits || 0)
            );
          }
        } else if (state.currentQuota && (action.payload?.status === 'CANCELLED' || action.payload?.status === 'EXPIRED')) {
          // Reset quota to free tier when subscription is cancelled or expired
          state.currentQuota.limitSeconds = 10800; // 3 hours (free tier)
          state.currentQuota.remainingSeconds = Math.max(0, 10800 - (state.currentQuota.usedSeconds || 0));
          state.currentQuota.limitCredits = 0;
          state.currentQuota.usedCredits = 0;
          state.currentQuota.remainingCredits = 0;
        }
      })
      .addCase(startFreeSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // toggleAutoRenew cases removed - requires stripe subscription ID

    // Admin actions
    builder
      .addCase(startTrial.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(startTrial.fulfilled, (state) => {
        state.adminLoading = false;
      })
      .addCase(startTrial.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload;
      });

    builder
      .addCase(upgradeSubscription.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(upgradeSubscription.fulfilled, (state) => {
        state.adminLoading = false;
      })
      .addCase(upgradeSubscription.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload;
      });

    builder
      .addCase(cancelSubscription.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(cancelSubscription.fulfilled, (state) => {
        state.adminLoading = false;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload;
      });

    // Feature config
    builder
      .addCase(fetchFeatureConfig.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(fetchFeatureConfig.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.featureConfig = action.payload;
      })
      .addCase(fetchFeatureConfig.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload;
      });

    builder
      .addCase(updateFeatureConfig.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(updateFeatureConfig.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.featureConfig = action.payload;
      })
      .addCase(updateFeatureConfig.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload;
      });

    // Billing actions
    builder
      .addCase(startBillingSubscription.pending, (state) => {
        state.billingLoading = true;
        state.billingError = null;
      })
      .addCase(startBillingSubscription.fulfilled, (state) => {
        state.billingLoading = false;
      })
      .addCase(startBillingSubscription.rejected, (state, action) => {
        state.billingLoading = false;
        state.billingError = action.payload;
      });

    builder
      .addCase(cancelBillingSubscription.pending, (state) => {
        state.billingLoading = true;
        state.billingError = null;
      })
      .addCase(cancelBillingSubscription.fulfilled, (state) => {
        state.billingLoading = false;
      })
      .addCase(cancelBillingSubscription.rejected, (state, action) => {
        state.billingLoading = false;
        state.billingError = action.payload;
      });

    builder
      .addCase(setBillingAutoRenew.pending, (state) => {
        state.billingLoading = true;
        state.billingError = null;
      })
      .addCase(setBillingAutoRenew.fulfilled, (state) => {
        state.billingLoading = false;
      })
      .addCase(setBillingAutoRenew.rejected, (state, action) => {
        state.billingLoading = false;
        state.billingError = action.payload;
      });

    // Admin users
    builder
      .addCase(fetchAdminUsers.pending, (state) => {
        state.adminLoading = true;
        state.adminError = null;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        state.adminLoading = false;
        state.adminUsers = action.payload;
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.adminLoading = false;
        state.adminError = action.payload;
      });
  }
});

export const { clearErrors, clearSubscription, updateQuotaWithSubscription, updateCreditsUsage, refreshSubscriptionData, resetQuotaToFreeTier } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
